import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  CssBaseline,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
  alpha,
  Popover,
  Paper,
  Button,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Menu as MenuIcon,
  Dashboard,
  Inventory,
  ShoppingCart,
  Category,
  LocalShipping,
  Assessment,
  Settings,
  Logout,
  AccountCircle,
  Payment,
  ManageAccounts,
  Home,
  Notifications,
  CheckCircle,
  AccessTime,
  Warning,
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getPedidos, Pedido } from '../../../services/pedidos';

const drawerWidth = 280;

// Styled Components
const StyledAppBar = styled(AppBar)({
  background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
  boxShadow: '0 4px 20px rgba(46, 90, 51, 0.2)',
  backdropFilter: 'blur(10px)',
});

const StyledDrawer = styled(Drawer)({
  '& .MuiDrawer-paper': {
    background: 'linear-gradient(180deg, #ffffff 0%, #f8fdf8 100%)',
    borderRight: 'none',
    boxShadow: '4px 0 20px rgba(0, 0, 0, 0.05)',
  },
});

const LogoContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '20px 16px',
  borderBottom: '1px solid rgba(46, 90, 51, 0.1)',
});

const LogoImage = styled('img')({
  width: '45px',
  height: '45px',
  borderRadius: '12px',
  boxShadow: '0 4px 12px rgba(46, 90, 51, 0.2)',
});

const MenuItemStyled = styled(ListItemButton)<{ component?: React.ElementType; to?: string }>(({ theme }) => ({
  margin: '4px 12px',
  borderRadius: '12px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha('#2e5a33', 0.08),
    transform: 'translateX(4px)',
  },
  '&.Mui-selected': {
    background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
    color: 'white',
    boxShadow: '0 4px 15px rgba(46, 90, 51, 0.3)',
    '&:hover': {
      background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
    },
    '& .MuiListItemIcon-root': {
      color: 'white',
    },
  },
}));

const UserCard = styled(Box)({
  margin: '16px',
  padding: '16px',
  borderRadius: '16px',
  background: 'linear-gradient(135deg, rgba(46, 90, 51, 0.05) 0%, rgba(46, 90, 51, 0.1) 100%)',
  border: '1px solid rgba(46, 90, 51, 0.1)',
});

interface LayoutProps {
  children: React.ReactNode;
}

interface MenuItemType {
  text?: string;
  icon?: React.ReactNode;
  path?: string;
  isExternal?: boolean;
  badge?: boolean;
  divider?: boolean;
}

const menuItems: MenuItemType[] = [
  { text: 'Ir a la Tienda', icon: <Home />, path: '/', isExternal: true },
  { divider: true },
  { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
  { text: 'Pedidos Online', icon: <LocalShipping />, path: '/dashboard/pedidos-online', badge: true },
  { text: 'Pagos', icon: <Payment />, path: '/dashboard/pagos' },
  { divider: true },
  { text: 'Productos', icon: <Inventory />, path: '/dashboard/productos' },
  { text: 'Categorías', icon: <Category />, path: '/dashboard/categorias' },
  { divider: true },
  { text: 'Usuarios', icon: <ManageAccounts />, path: '/dashboard/usuarios' },
];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationsAnchor, setNotificationsAnchor] = useState<null | HTMLElement>(null);
  const [pedidosPendientes, setPedidosPendientes] = useState<Pedido[]>([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Cargar pedidos pendientes para notificaciones
  useEffect(() => {
    const cargarPedidosPendientes = async () => {
      try {
        const pedidos = await getPedidos();
        const pendientes = pedidos.filter(p => 
          p.estado === 'pendiente' || p.estado === 'confirmado' || p.estado === 'preparando'
        );
        setPedidosPendientes(pendientes.slice(0, 5)); // Solo mostrar últimos 5
        setNotificationCount(pendientes.length);
      } catch (error) {
        console.error('Error al cargar notificaciones:', error);
      }
    };

    cargarPedidosPendientes();
    // Actualizar cada 30 segundos
    const interval = setInterval(cargarPedidosPendientes, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleNotificationsClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchor(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchor(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
    handleClose();
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'pendiente': return '#ff8f00';
      case 'confirmado': return '#1976d2';
      case 'preparando': return '#9c27b0';
      default: return '#757575';
    }
  };

  const getEstadoIcon = (estado: string) => {
    switch (estado) {
      case 'pendiente': return <Warning sx={{ fontSize: 18, color: '#ff8f00' }} />;
      case 'confirmado': return <CheckCircle sx={{ fontSize: 18, color: '#1976d2' }} />;
      case 'preparando': return <AccessTime sx={{ fontSize: 18, color: '#9c27b0' }} />;
      default: return <LocalShipping sx={{ fontSize: 18 }} />;
    }
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo */}
      <LogoContainer>
        <LogoImage
          src="/assets/logos/logo-el-pinon-nuevo.png"
          alt="El Piñón"
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            e.currentTarget.style.display = 'none';
          }}
        />
        <Box>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700, 
              color: '#2e5a33',
              fontFamily: "'Playfair Display', serif",
              lineHeight: 1.2,
            }}
          >
            El Piñón
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Panel de Administración
          </Typography>
        </Box>
      </LogoContainer>

      {/* Menu Items */}
      <List sx={{ flexGrow: 1, py: 2 }}>
        {menuItems.map((item, index) => {
          if (item.divider) {
            return <Divider key={`divider-${index}`} sx={{ my: 1, mx: 2 }} />;
          }

          return (
            <ListItem key={item.text} disablePadding>
              <MenuItemStyled
                component={Link}
                to={item.path || '/'}
                selected={!item.isExternal && location.pathname === item.path}
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: item.isExternal 
                      ? '#2e5a33' 
                      : location.pathname === item.path 
                        ? 'inherit' 
                        : 'rgba(0, 0, 0, 0.6)',
                  }}
                >
                  {item.badge ? (
                    <Badge badgeContent={3} color="error">
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: location.pathname === item.path ? 600 : 500,
                    fontSize: '0.95rem',
                  }}
                />
              </MenuItemStyled>
            </ListItem>
          );
        })}
      </List>

      {/* User Card */}
      {user && (
        <UserCard>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar 
              sx={{ 
                width: 44, 
                height: 44,
                background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
                fontWeight: 600,
              }}
            >
              {getInitials(user.nombre_completo || user.first_name || 'U')}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography 
                variant="subtitle2" 
                sx={{ fontWeight: 600, color: '#2e5a33' }}
                noWrap
              >
                {user.nombre_completo || user.first_name || 'Usuario'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {user.email}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Tooltip title="Configuración">
              <IconButton size="small" sx={{ flex: 1, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.8)' }}>
                <Settings fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cerrar Sesión">
              <IconButton 
                size="small" 
                onClick={handleLogout}
                sx={{ 
                  flex: 1, 
                  borderRadius: 2, 
                  bgcolor: 'rgba(255,255,255,0.8)',
                  color: 'error.main',
                  '&:hover': { bgcolor: 'error.light', color: 'white' },
                }}
              >
                <Logout fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </UserCard>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7f5' }}>
      <CssBaseline />
      
      {/* AppBar */}
      <StyledAppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Box>
              <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
                {menuItems.find(item => item.path === location.pathname)?.text || 'Dashboard'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, display: { xs: 'none', sm: 'block' } }}>
                Gestión de El Piñón
              </Typography>
            </Box>
          </Box>
          
          {/* Right Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Notificaciones">
              <IconButton color="inherit" onClick={handleNotificationsClick}>
                <Badge badgeContent={notificationCount} color="error">
                  <Notifications />
                </Badge>
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Mi cuenta">
              <IconButton
                onClick={handleMenu}
                sx={{
                  p: 0.5,
                  border: '2px solid rgba(255,255,255,0.3)',
                  '&:hover': { border: '2px solid rgba(255,255,255,0.6)' },
                }}
              >
                <Avatar 
                  sx={{ 
                    width: 36, 
                    height: 36,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    fontWeight: 600,
                  }}
                >
                  {user ? getInitials(user.nombre_completo || user.first_name || 'U') : 'U'}
                </Avatar>
              </IconButton>
            </Tooltip>
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  minWidth: 200,
                  borderRadius: 2,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {user?.nombre_completo || 'Usuario'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email}
                </Typography>
              </Box>
              <MenuItem onClick={handleClose} sx={{ py: 1.5 }}>
                <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon>
                Mi Perfil
              </MenuItem>
              <MenuItem onClick={handleClose} sx={{ py: 1.5 }}>
                <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
                Configuración
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: 'error.main' }}>
                <ListItemIcon><Logout fontSize="small" color="error" /></ListItemIcon>
                Cerrar Sesión
              </MenuItem>
            </Menu>

            {/* Popover de Notificaciones */}
            <Popover
              open={Boolean(notificationsAnchor)}
              anchorEl={notificationsAnchor}
              onClose={handleNotificationsClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: {
                  mt: 1.5,
                  width: 360,
                  maxHeight: 450,
                  borderRadius: 3,
                  boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                },
              }}
            >
              <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.08)', background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                  Notificaciones
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  {notificationCount} pedidos requieren atención
                </Typography>
              </Box>
              
              <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
                {pedidosPendientes.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <CheckCircle sx={{ fontSize: 48, color: '#4caf50', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No hay pedidos pendientes
                    </Typography>
                  </Box>
                ) : (
                  pedidosPendientes.map((pedido) => (
                    <Paper
                      key={pedido.id}
                      sx={{
                        m: 1,
                        p: 2,
                        cursor: 'pointer',
                        borderRadius: 2,
                        border: '1px solid rgba(0,0,0,0.05)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          background: 'rgba(46, 90, 51, 0.05)',
                          transform: 'translateX(4px)',
                        },
                      }}
                      onClick={() => {
                        handleNotificationsClose();
                        navigate('/dashboard/pedidos-online');
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: `${getEstadoColor(pedido.estado)}20`,
                          }}
                        >
                          {getEstadoIcon(pedido.estado)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                            Pedido #{pedido.numero_pedido}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {pedido.cliente_detalle?.nombre || 'Cliente'} • ${parseFloat(pedido.total).toLocaleString('es-CL')}
                          </Typography>
                        </Box>
                        <Chip
                          label={pedido.estado}
                          size="small"
                          sx={{
                            bgcolor: `${getEstadoColor(pedido.estado)}15`,
                            color: getEstadoColor(pedido.estado),
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      </Box>
                    </Paper>
                  ))
                )}
              </Box>
              
              <Box sx={{ p: 1.5, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    handleNotificationsClose();
                    navigate('/dashboard/pedidos-online');
                  }}
                  sx={{
                    background: 'linear-gradient(135deg, #2e5a33 0%, #3d7342 100%)',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Ver todos los pedidos
                </Button>
              </Box>
            </Popover>
          </Box>
        </Toolbar>
      </StyledAppBar>

      {/* Drawer Mobile */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <StyledDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: drawerWidth },
          }}
        >
          {drawer}
        </StyledDrawer>
        
        {/* Drawer Desktop */}
        <StyledDrawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { width: drawerWidth },
          }}
          open
        >
          {drawer}
        </StyledDrawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          pt: { xs: 8, md: 9 },
          pb: 4,
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
